<script setup lang="ts">import { createPropsRestProxy } from '/vue-macros/reactivity-transform/helper';
const props = createPropsRestProxy(__props, ["file","isPopout","scale"]);

import { useRouter } from "vue-router";
import { computed, provide, ref } from "vue";
import { ToolbarButton, ToolbarRoot, ToolbarToggleGroup, ToolbarToggleItem } from "reka-ui";

import { requestTranscriptRender } from "@/services/CaseFileService";
import { useMessageStore } from "@/stores/message-store";

import VoiceTranscriptMain from "./VoiceTranscriptMain.vue";
import VoiceTranscriptPanelSearch from "./VoiceTranscriptPanelSearch.vue";
import VoiceTranscriptPanelAnnotations from "./VoiceTranscriptPanelAnnotations.vue";

import RsLoading from "@/components/Loading.vue";
import ConfirmPopover from "@/components/ConfirmPopover.vue";
import StringTooltip from "@/components/Tooltip/StringTooltip.vue";
import SummaryThemesLegend from "@/components/CasePanels/RecordView/SummaryThemesLegend.vue";
import ViewerTranscriptPanelRelated from "@/components/CasePanels/ViewerTranscriptPanelRelated.vue";

import { CASE_ROUTE_PARAMS_KEY } from "@/hooks/use-case-route-params";
import { useDirectlyRelatedFiles } from "@/hooks/use-directly-related-files";

import { isRedaction } from "@/services/AnnotationService";
import { useCaseFileStore } from "@/stores/case-file-store";
import { useAnnotationStore } from "@/stores/annotation-store";
import { $can, isTranscriptAnnotation } from "@/services/UtilitiesService";

import { Panels } from "@/models/PanelEnums";

import type { Metadata } from "@/models/Metadata";
import type { DockviewPanelComponentEmits } from "@/hooks/use-case-panel";
import type { InjectedCaseRouteParams } from "@/hooks/use-case-route-params";

withDefaults(defineProps<{
  isPopout: boolean;
  isVisible: boolean;
  file: Metadata;
  routeParams: InjectedCaseRouteParams;
  scale: { width: number; height: number };
}>(), {  })
const emit = defineEmits<DockviewPanelComponentEmits>();

const router = useRouter();
const relatedFiles = useDirectlyRelatedFiles(__props.file);

const mode = ref<CURSOR_MODE>("PAN");
const activePanel = ref<Record<string, boolean>>({});
const mainTranscriptContainer = ref<InstanceType<typeof VoiceTranscriptMain>>();

const containerWidthTrigger = ref<string>();

const routeParams = computed(() => props.routeParams);

const currentPanelGlobal = computed(() => {
  const state = useCaseFileStore().currentPanelGlobal;
  return { ...state.transcript, ...state.shared };
});

const transcript = computed(() => useCaseFileStore().cachedFilesData[__props.file.fileReference]?.transcript);

const transcriptPanels = computed(() => {
  type EmitsToProps<T extends Record<string, (...args: any) => any>> = keyof T extends `on${infer C}`
    ? { [K in Uncapitalize<C>]: T[`on${Capitalize<K>}`] | undefined }
    : never;

  interface TranscriptPanel<C extends abstract new (...args: any) => any> {
    key: string;
    show: boolean;
    icon: string;
    title: string;
    showPanel: boolean;
    panelComponent: C;
    panelComponentProps: InstanceType<C>["$props"];
    panelComponentEmits: EmitsToProps<Pick<InstanceType<C>, Extract<keyof InstanceType<C>, `on${string}`>>>;
  }

  if (__props.file.fileReference == null) return [];

  function showPanel(key: string) {
    return activePanel.value[key] || currentPanelGlobal.value[key];
  }

  function addPanel<C extends abstract new (...args: any) => any>(panel: TranscriptPanel<C>): TranscriptPanel<C> {
    return panel;
  }

  return [
    addPanel({
      key: Panels.shared.SEARCH,
      show: true,
      icon: "fas fa-search",
      title: "Search",
      panelComponent: VoiceTranscriptPanelSearch,
      panelComponentProps: {
        utterances: transcript.value?.utterances ?? [],
        fileReference: __props.file.fileReference,
      },
      panelComponentEmits: {},
      get showPanel(): boolean {
        return this.show && showPanel(this.key);
      },
    }),
    addPanel({
      key: Panels.shared.ANNOTATIONS,
      show: !__props.isPopout,
      icon: "fas fa-pen",
      title: "Marks",
      panelComponent: VoiceTranscriptPanelAnnotations,
      panelComponentProps: {
        fileReference: __props.file.fileReference,
        annotation: routeParams.value.annotation,
      },
      panelComponentEmits: {},
      get showPanel(): boolean {
        return this.show && showPanel(this.key);
      },
    }),
    addPanel({
      key: Panels.shared.RELATED,
      show:
        (__props.file.parent != null ||
          relatedFiles.value.unfilteredFiles.children.length > 0 ||
          relatedFiles.value.unfilteredFiles.siblings.length > 0) &&
        !__props.isPopout,
      icon: "fas fa-file-lines",
      title: "Related",
      panelComponent: ViewerTranscriptPanelRelated,
      panelComponentProps: {
        parentFileReference: __props.file.parent,
        attachments: relatedFiles.value,
        class: "!tw-w-1/4",
      },
      panelComponentEmits: {},
      get showPanel(): boolean {
        return this.show && showPanel(this.key);
      },
    }),
  ];
});

const panelWidth = computed(() => {
  void containerWidthTrigger.value;

  const containerEL = mainTranscriptContainer.value?.container;
  if (containerEL == null) return __props.scale.width;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  return +getComputedStyle(containerEL).width.replace("px", "") || __props.scale.width;
});

const panelTools = computed(() => {
  return [
    { icon: "far fa-hand-paper", key: "PAN", title: "Pan Tool" },
    { icon: "fas fa-highlighter", key: "CREATE-MARK", title: "Create Mark Tool" },
    { icon: "fas fa-italic", key: "SELECT", title: "Copy Text Tool" },
    { icon: "fas fa-eraser", key: "REDACT", title: "Redact Area Tool", show: $can("redact") },
  ] satisfies Array<{ icon: string; key: CURSOR_MODE; title: string; show?: boolean }>;
});

const hasRedactions = computed(
  () =>
    useAnnotationStore().unfilteredAnnotations.find(
      (a) => a.parsedMarkData != null && a.fileReference === __props.file.fileReference && isRedaction(a.parsedMarkData),
    ) != null,
);

provide(CASE_ROUTE_PARAMS_KEY, routeParams);

function togglePanel(panel: string) {
  if (activePanel.value[panel]) {
    activePanel.value[panel] = false;
    useCaseFileStore().currentTranscriptPanelGlobalChanged(panel, true);
  } else if (!activePanel.value[panel]) {
    activePanel.value[panel] = !currentPanelGlobal.value[panel];
    useCaseFileStore().currentTranscriptPanelGlobalChanged(panel, false);
  }

  setTimeout(() => (containerWidthTrigger.value = Math.random().toString(36)));
}

function changeMode(newMode: CURSOR_MODE) {
  if (newMode === "POP-OUT") emit("panelPopoutAction", { time: routeParams.value.time });
  else mode.value = newMode;
}

async function downloadTranscript(): Promise<void> {
  if (transcript.value?.status !== "success" || (__props.file.fileType !== "audio" && __props.file.fileType !== "ac5")) return;

  try {
    await requestTranscriptRender(
      routeParams.value.client,
      routeParams.value.brand,
      routeParams.value.casename,
      __props.file.fileReference,
    );

    useMessageStore().updateMessage({
      type: "success",
      title: "Render Transcript",
      message: "Your request to render the transcript has been logged, check your jobs list to see its progress.",
      autoHide: true,
      showReload: false,
    });
  } catch {
    useMessageStore().updateMessage({
      type: "error",
      title: "Render Transcript",
      message:
        "An error occurred while we were requesting the transcript render.  If this continues, please contact your support team.",
      autoHide: true,
      showReload: false,
    });
  }
}

async function commitRedaction() {
  if (__props.isPopout) return;

  try {
    await useAnnotationStore().redact({ fileReference: __props.file.fileReference });

    // Deselect the selected annotation if it is to be redacted
    const selectedAnnotation =
      routeParams.value.annotation == null
        ? undefined
        : useAnnotationStore().annotations.find((a) => a.markId === routeParams.value.annotation);

    if (selectedAnnotation != null) {
      try {
        if (
          isTranscriptAnnotation(selectedAnnotation.parsedMarkData) &&
          selectedAnnotation.parsedMarkData.type === "TranscriptRedaction"
        ) {
          router.push({
            name: "casefile",
            params: {
              brand: routeParams.value.brand,
              casename: routeParams.value.casename,
              casefile: __props.file.fileReference,
            },
          });
        }
      } catch (error) {}
    }

    useCaseFileStore().setCommittedRedactionsState(__props.file.fileReference, true);
  } catch {}
}

export type CURSOR_MODE = "PAN" | "CREATE-MARK" | "SELECT" | "POP-OUT" | "REDACT";
</script>
<template>
  <div class="content-visibility-auto tw-flex tw-size-full tw-flex-col tw-items-center tw-justify-center">
    <RsLoading v-if="transcript == null || transcript.status === 'loading'" id="loading" message="Loading file data" />
    <div v-else-if="transcript.status === 'error'" class="tw-grid tw-size-full tw-place-items-center">
      <div class="tw-max-w-sm tw-text-pretty">Error loading transcript</div>
    </div>
    <div
      v-else-if="transcript.status === 'not-exists' || transcript.utterances.length === 0"
      class="tw-grid tw-size-full tw-place-items-center"
    >
      <div class="tw-max-w-sm tw-text-pretty">No transcript exists for this file</div>
    </div>
    <template v-else>
      <ToolbarRoot
        class="custom-controls custom-controls-voice tw-z-10 tw-flex tw-w-full tw-grid-cols-[1fr_max-content_1fr] tw-flex-col tw-items-center tw-justify-center tw-gap-x-2 tw-gap-y-1 tw-px-2 tw-py-2 *:tw-min-w-0"
      >
        <div
          class="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-1 *:tw-flex *:tw-items-center *:tw-justify-start *:tw-gap-1 *:tw-rounded-sm *:tw-border *:tw-border-transparent *:tw-px-1 [&>*:focus]:tw-border-current [&>*:hover]:tw-border-current"
        >
          <template v-for="k in transcriptPanels" :key="k.key">
            <ToolbarButton
              v-if="k.show"
              :aria-label="`Open ${k.title} Panel`"
              :class="{
                'tw-bg-[#676767] tw-shadow': activePanel[k.key],
                '!tw-border-gray-300': currentPanelGlobal[k.key],
              }"
              @click="() => togglePanel(k.key)"
            >
              <i aria-hidden="true" :class="k.icon" />
              <span>{{ k.title }} </span>
            </ToolbarButton>
          </template>
        </div>
        <div v-if="!isPopout" class="tw-flex tw-grow tw-items-center tw-justify-center">
          <div class="options-container tw-flex tw-flex-wrap tw-justify-center">
            <StringTooltip item="Open in Popup Window" #="{ item }">
              <ToolbarButton class="option" :aria-label="item" @click="changeMode('POP-OUT')">
                <i aria-hidden="true" class="fas fa-external-link-alt" />
              </ToolbarButton>
            </StringTooltip>
            <ToolbarToggleGroup
              aria-label="Current Mouse Tool"
              :model-value="mode"
              class="tw-contents"
              @update:model-value="(val) => changeMode(val as CURSOR_MODE)"
            >
              <StringTooltip v-for="k in panelTools" :key="k.key" :item="k.title" #="{ item }">
                <ToolbarToggleItem
                  v-if="k.show ?? true"
                  class="option"
                  :value="k.key"
                  :aria-label="item"
                  :class="{ active: mode === k.key }"
                  @click="changeMode(k.key)"
                >
                  <i aria-hidden="true" :class="k.icon" />
                </ToolbarToggleItem>
              </StringTooltip>
            </ToolbarToggleGroup>
          </div>
        </div>
        <div
          class="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-1 *:tw-flex *:tw-items-center *:tw-justify-start *:tw-gap-1 *:tw-rounded-sm *:tw-border *:tw-border-transparent *:tw-px-1 [&>*:focus]:tw-border-current [&>*:hover]:tw-border-current"
        >
          <button type="button" @click="downloadTranscript">
            <i aria-hidden="true" class="fas fa-download" />
            Download
          </button>
          <ConfirmPopover
            v-if="$can('redact') && hasRedactions && !isPopout"
            confirm-text="Commit"
            :trigger-props="{ asChild: true }"
            @confirmed="commitRedaction"
          >
            <template #trigger>
              <ToolbarButton>
                <i aria-hidden="true" class="fas fa-eraser" />
                Commit Redactions
              </ToolbarButton>
            </template>
            <template #question>
              Are you sure? This will commit the redaction masks to the record. The original audio uploaded will be
              permanently destroyed and replaced with the sections of audio silenced. Access to this record will be
              temporarily suspended whilst the redactions are being committed.
            </template>
          </ConfirmPopover>
        </div>
      </ToolbarRoot>
      <SummaryThemesLegend :file-reference="file.fileReference" />
      <VoiceTranscriptMain
        ref="mainTranscriptContainer"
        :mode="mode"
        :is-visible="isVisible"
        :panel-width="panelWidth"
        :utterances="transcript.utterances"
        :file-reference="file.fileReference"
      >
        <template #panels>
          <template v-for="panel in transcriptPanels" :key="panel.key">
            <component
              :is="panel.panelComponent"
              v-if="panel.showPanel"
              :="panel.panelComponentProps"
              v-on="panel.panelComponentEmits"
            />
          </template>
        </template>
      </VoiceTranscriptMain>
    </template>
  </div>
</template>
